import React, { FC } from 'react';
import { Box, Paper, Typography, useTheme } from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { NetCancellationStatistics } from '@/types/NetCancellationStatistics';
import { getNetCancellationBreakdown } from './netCancellationRate';

interface NetCancellationRateWidgetProps {
  statistics?: NetCancellationStatistics | null;
  loading?: boolean;
  failed?: boolean;
}

const formatCount = (count: number | undefined) => (count === undefined ? '—' : count.toLocaleString('en-US'));

/** Query-free presentation of the period's case counts and patient-level coverage. */
const NetCancellationRateWidget: FC<NetCancellationRateWidgetProps> = ({
  statistics,
  loading = false,
  failed = false,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const breakdown = loading || failed ? null : getNetCancellationBreakdown(statistics);
  const segments = [
    { name: 'Net cancelled', value: breakdown?.netCancelledCases, color: theme.palette.primary.main },
    { name: 'Rescheduled', value: breakdown?.rescheduledCases, color: theme.palette.success.main },
    { name: 'Other scheduled', value: breakdown?.otherScheduledCases, color: theme.palette.text.disabled },
  ];
  const rateLabel = breakdown?.rate == null ? '—' : `${breakdown.rate.toFixed(1)}%`;
  const subtitle = loading
    ? 'Loading…'
    : failed
      ? 'Unable to load'
      : breakdown
        ? `${formatCount(breakdown.totalScheduledCases)} scheduled`
        : 'Unavailable';
  const accessibleSummary = breakdown
    ? `Net cancellation rate ${breakdown.rate == null ? 'not applicable' : rateLabel}; ${breakdown.totalScheduledCases} scheduled cases; ${breakdown.cancelledCases} cancelled in total. ${segments.map(s => `${s.value} ${s.name.toLowerCase()}`).join(', ')}.`
    : `Net cancellation rate: ${subtitle.toLowerCase()}.`;

  return (
    <Box className={classes.root} aria-busy={loading}>
      <Box className={classes.chart} role="img" aria-label={accessibleSummary}>
        <Box className={classes.plot}>
          {breakdown && breakdown.totalScheduledCases > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                <Pie
                  data={segments}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  startAngle={90}
                  endAngle={-270}
                  innerRadius="50%"
                  outerRadius="70%"
                  stroke="none"
                  isAnimationActive={false}
                >
                  {segments.map(segment => (
                    <Cell key={segment.name} fill={segment.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const segment = payload[0];
                    return (
                      <Paper>
                        <Box p={1.5}>
                          <Typography variant="caption">{segment.name}</Typography>
                          <Typography>{formatCount(Number(segment.value))} cases</Typography>
                        </Box>
                      </Paper>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <svg width="100%" height="100%" viewBox="0 0 100 100" aria-hidden="true">
              <circle
                cx="50"
                cy="50"
                r="30"
                fill="none"
                stroke={alpha(theme.palette.text.primary, 0.12)}
                strokeWidth="10"
              />
            </svg>
          )}
        </Box>
        <Box className={classes.center} aria-hidden="true">
          <Typography variant="h4" noWrap className={classes.rate}>
            {rateLabel}
          </Typography>
          {breakdown && (
            <Typography variant="caption" component="p" noWrap className={classes.cancelled}>
              {formatCount(breakdown.cancelledCases)} cancelled
            </Typography>
          )}
          <Typography variant="caption" component="p" noWrap className={breakdown ? classes.scheduled : classes.subtitle}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
      <Box component="ul" className={classes.legend} aria-label="Case categories">
        {segments.map(segment => (
          <Box component="li" key={segment.name} className={classes.legendItem}>
            <span className={classes.swatch} style={{ backgroundColor: segment.color }} aria-hidden="true" />
            <Typography variant="caption" className={classes.legendLabel}>
              {segment.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatCount(segment.value)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: { display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minWidth: 0 },
  chart: { position: 'relative', width: '100%', paddingTop: '100%' },
  plot: { position: 'absolute', inset: 0 },
  center: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    pointerEvents: 'none',
    width: '48%',
  },
  rate: { fontVariantNumeric: 'tabular-nums', fontSize: 28, lineHeight: 1.2 },
  cancelled: { marginTop: theme.spacing(0.5), fontWeight: 500 },
  scheduled: { color: theme.palette.text.secondary },
  subtitle: { color: theme.palette.text.secondary, marginTop: theme.spacing(1), fontWeight: 500 },
  legend: {
    listStyle: 'none',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    margin: 0,
    marginTop: 'auto',
    padding: 0,
    paddingTop: theme.spacing(1),
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: theme.spacing(0.5), whiteSpace: 'nowrap' },
  swatch: { width: 12, height: 12, borderRadius: '50%', flexShrink: 0 },
  legendLabel: { fontWeight: 500, lineHeight: 1 },
}));

export default NetCancellationRateWidget;
